import React, { useState } from 'react'

import { uniq } from 'lodash'
import { FiPlus, FiGrid, FiList } from 'react-icons/fi'

import { useAuth, useDashboard } from '@/hooks'
import { categoryName, isInsightAllowed } from '@/insights/insightCatalogue'
import { insights } from '@/insights/insights'

interface InsightAddPanelProps {
  onChoose: (slug: string) => void
}

export const ChooseInsight: React.FC<InsightAddPanelProps> = ({ onChoose }) => {
  const dashboardContext = useDashboard()
  const { insights: allowedInsights } = useAuth()
  // The token's `insights` claim is the per-user/per-company entitlement; a newly
  // registered insight is off for everyone until an admin enables it.
  const availableInsights = Object.values(insights).filter((i) => isInsightAllowed(allowedInsights, i.slug))
  const dashboard = dashboardContext.currentDashboard
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [search, setSearch] = useState('')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')

  const allCategories = ['all', ...uniq(availableInsights.map((i) => i.category).sort())]

  // Category first, then the search box narrows by name or description; always alphabetical.
  const getFilteredInsights = () => {
    const q = search.trim().toLowerCase()
    return availableInsights
      .filter((i) => selectedCategory === 'all' || i.category === selectedCategory)
      .filter((i) => !q || i.name.toLowerCase().includes(q) || i.description.toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name))
  }

  const countBySlug = React.useMemo(() => {
    return dashboard.insights.reduce<Record<string, number>>((h, i) => {
      if (!h[i.slug]) {
        h[i.slug] = 0
      }
      h[i.slug] += 1
      return h
    }, {})
  }, [dashboard])

  return [
    <div key="view-select" className="p-4 border-b border-base-300">
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setViewMode('grid')}
          className={`btn btn-sm ${viewMode === 'grid' ? 'btn-primary' : 'btn-ghost'}`}
        >
          <FiGrid className="w-4 h-4" />
        </button>
        <button
          onClick={() => setViewMode('list')}
          className={`btn btn-sm ${viewMode === 'list' ? 'btn-primary' : 'btn-ghost'}`}
        >
          <FiList className="w-4 h-4" />
        </button>
      </div>

      <select
        value={selectedCategory}
        onChange={(e) => setSelectedCategory(e.target.value)}
        className="select select-bordered w-full"
      >
        {allCategories.map((category) => (
          <option key={category} value={category}>
            {category === 'all' ? 'All Insights' : categoryName(category)}
          </option>
        ))}
      </select>
      <input
        type="search"
        className="input input-bordered w-full mt-2"
        placeholder="Search insights..."
        aria-label="Search insights"
        data-testid="insight-search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
    </div>,

    <div key="insight-list" className="flex-1 overflow-y-auto p-4">
      <div className={`${viewMode === 'grid' ? 'grid grid-cols-1 gap-4' : 'space-y-2'}`}>
        {getFilteredInsights().length === 0 && (
          <p className="text-sm text-base-content/60 text-center" data-testid="insight-search-empty">
            No insights match.
          </p>
        )}
        {getFilteredInsights().map((insight) => {
          const insightCount = countBySlug[insight.slug]
          return (
            <div
              key={insight.slug}
              className={`border border-base-300 rounded-lg overflow-hidden ${
                viewMode === 'grid' ? 'bg-base-200' : 'bg-transparent'
              }`}
            >
              {viewMode === 'grid' ? (
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2 float-end">
                    <div className="text-2xl">{insight.icon}</div>
                    <div className="flex items-center gap-2">
                      {insightCount > 0 && <span className="badge badge-primary badge-sm">{insightCount}</span>}
                      <button onClick={() => onChoose(insight.slug)} className="btn btn-primary btn-sm">
                        <FiPlus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  <h3 className="font-semibold text-sm mb-1">{insight.name}</h3>
                  <p className="text-xs text-base-content/70">{insight.description}</p>
                  <div className="mt-2">
                    <span className="badge badge-outline badge-xs">{categoryName(insight.category)}</span>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => onChoose(insight.slug)}
                  className="flex items-center w-full hover:cursor-pointer gap-3 p-3 hover:bg-base-200 transition-colors"
                >
                  {insight.icon && <div className="text-xl">{insight.icon}</div>}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-sm truncate">{insight.name}</h3>
                      {insightCount > 0 && <span className="badge badge-primary badge-xs">{insightCount}</span>}
                    </div>
                    <p className="text-xs text-base-content/70 truncate text-left">{insight.description}</p>
                  </div>
                </button>
              )}
            </div>
          )
        })}
      </div>
    </div>,

    <div key="footer" className="p-4 border-t border-base-300">
      <div className="text-sm text-base-content/70 text-center">
        {dashboard.insights?.length || 0} Insight{(dashboard.insights?.length || 0) === 1 ? '' : 's'} on dashboard
      </div>
    </div>,
  ]
}
